/**
 * RTK Query merge for listMessages — keeps pagination logic testable (anchor guard from e6b2847d0).
 *
 * End-of-history is derived from page length, not `totalCount`:
 * if a `beforeId`/`fromId` page returns fewer than `MESSAGES_PER_PAGE` items, there is no more
 * history in that direction. Flags `hasOlder` / `hasNewer` are stashed on the merged cache so
 * `MessagesList` can drive infinite scroll from them (OMM-603).
 */

/** Must match the back-end `perPage` default in MessageController.getMessages. */
export const MESSAGES_PER_PAGE = 20;

export const dedupeById = arr => {
  const seen = new Set();
  return arr.filter(m => {
    if (!m?._id) return false;
    if (seen.has(m._id)) return false;
    seen.add(m._id);
    return true;
  });
};

const pageRange = msgs => {
  const pages = (msgs || [])
    .map(m => {
      const match = typeof m?.text === 'string' ? m.text.match(/EX-PAGE-(\d+)/) : null;
      return match ? Number(match[1]) : null;
    })
    .filter(n => n != null);
  if (!pages.length) return null;
  return { min: Math.min(...pages), max: Math.max(...pages), count: pages.length };
};

/**
 * @param {object} cache - previous cache entry { messages, hasOlder?, hasNewer?, ... }
 * @param {object} incoming - API slice for this page { messages, ... }
 * @param {object} arg - { channelId, beforeId?, fromId?, aroundId? }
 */
export function mergeMessagesPages(cache, incoming, arg) {
  const incomingLen = incoming?.messages?.length ?? 0;
  const reachedEnd = incomingLen < MESSAGES_PER_PAGE;
  const cacheRange = pageRange(cache?.messages);
  const incomingRange = pageRange(incoming?.messages);

  if (!arg.beforeId && !arg.fromId && !arg.aroundId) {
    if (cache?.awaitingAroundId) {
      return cache;
    }
    // Tail fetch racing an aroundId window must not replace the anchor cache (OMM-632).
    if (cache?.messages?.length && incoming?.messages?.length) {
      const overlaps = incoming.messages.some(m => cache.messages.some(c => c._id === m._id));
      if (!overlaps) {
        const isAnchorWindow = cache.hasNewer === true && cache.hasOlder !== false;
        if (isAnchorWindow) {
          const cacheNewest = cache.messages[0]?._id;
          const cacheOldest = cache.messages[cache.messages.length - 1]?._id;
          const incomingNewest = incoming.messages[0]?._id;
          const incomingOldest = incoming.messages[incoming.messages.length - 1]?._id;
          if (cacheNewest && incomingOldest && incomingOldest > cacheNewest) {
            return { ...cache, hasNewer: true };
          }
          if (cacheOldest && incomingNewest && incomingNewest < cacheOldest) {
            return { ...cache, hasOlder: true };
          }
        }
      }
      if (!overlaps && incomingRange && cacheRange && incomingRange.min > cacheRange.max + 1) {
        return cache;
      }
      const cacheNewest = cache.messages[0]?._id;
      const incomingNewest = incoming.messages[0]?._id;
      if (!overlaps && !incomingRange && incomingNewest > cacheNewest) {
        return cache;
      }
    }
    return { ...incoming, hasOlder: !reachedEnd, hasNewer: false };
  }

  if (arg.aroundId) {
    // Search/deep-link: replace the window entirely — never merge with pages from
    // the previous scroll position (would create gaps like 023–025 … 131–133).
    return {
      ...incoming,
      messages: [...(incoming.messages || [])],
      hasOlder: true,
      hasNewer: true,
      awaitingAroundId: false,
    };
  }

  const anchor = arg.beforeId || arg.fromId;
  if (anchor && !cache?.messages?.some(m => m._id === anchor)) {
    // The anchor we paginated from is not in the cache. This means the request was
    // built from a stale/cross-channel anchor (e.g. fired during a channel switch
    // while RTK still held the previous channel's data). Replacing the cache with
    // this non-contiguous page corrupts history and can falsely collapse hasOlder
    // (OMM-603), so keep the existing cache untouched and ignore the incoming page.
    if (cache?.messages?.length) {
      return cache;
    }
    // Cache was cleared for an aroundId jump — drop in-flight pagination instead of
    // seeding a disjoint page that would gap against the incoming anchor window.
    if (cache?.awaitingAroundId) {
      return cache;
    }
    // No usable cache yet — fall back to the incoming page so the channel can render.
    return {
      ...incoming,
      hasOlder: arg.beforeId ? !reachedEnd : (cache?.hasOlder ?? true),
      hasNewer: arg.fromId ? !reachedEnd : (cache?.hasNewer ?? false),
    };
  }

  if (arg.beforeId) {
    const cacheOldest = cache.messages?.[cache.messages.length - 1]?._id;
    if (cacheOldest && arg.beforeId !== cacheOldest) {
      return cache;
    }
    return {
      ...incoming,
      messages: dedupeById([...(cache.messages || []), ...incoming.messages]),
      hasOlder: !reachedEnd,
      hasNewer: cache?.hasNewer ?? false,
    };
  }

  const temps = (cache.messages || []).filter(message => message.temp);
  const persistedCache = (cache.messages || []).filter(message => !message.temp);
  const newestPersistedId = persistedCache[0]?._id;
  // fromId is already known to be in the cache. It is not the newest row when a
  // socket message (or an optimistic row) landed while this page was in flight.
  if (newestPersistedId && arg.fromId !== newestPersistedId) {
    const persisted = dedupeById([
      ...(incoming.messages || []).filter(message => !message.temp),
      ...persistedCache,
    ]);
    const ordered = persisted.every(message => /^[a-f0-9]{24}$/i.test(String(message._id)))
      ? [...persisted].sort((a, b) => (a._id < b._id ? 1 : -1))
      : persisted;
    return {
      ...incoming,
      messages: [...temps, ...ordered],
      hasOlder: cache?.hasOlder ?? true,
      hasNewer: !reachedEnd,
    };
  }
  if (incomingRange && cacheRange && incomingRange.min > cacheRange.max + 1) {
    return { ...cache, hasNewer: false };
  }

  return {
    ...incoming,
    messages: dedupeById([
      ...temps,
      ...(incoming.messages || []).filter(message => !message.temp),
      ...persistedCache,
    ]),
    hasOlder: cache?.hasOlder ?? true,
    hasNewer: !reachedEnd,
  };
}
